import firebase from '../../firebase';
import 'firebase/compat/auth';
import {
  CONNECT_USER_TO_LICHESS,
  SET_USER,
  SET_ERRORS,
  CLEAR_ERRORS,
  LOADING_UI,
  SET_UNAUTHENTICATED,
  LOADING_USER,
  MARK_NOTIFICATIONS_READ,
  SET_AUTHENTICATED
} from '../types';
import axios from 'axios';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail, createUserWithEmailAndPassword, updateProfile, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
// Rate limiting
import { checkRateLimit } from '../../util/rateLimiter';
import { getRateLimit, buildGlobalRateKey } from '../../util/rateLimitConfig';

export const loginUser = (userData, history) => async (dispatch) => {
  dispatch({ type: LOADING_UI });
  const auth = getAuth();

  try {
    let emailToUse = userData.identifier;

    // If the input doesn't look like an email, treat it as a username (handle)
    if (!emailToUse.includes('@')) {
      const db = getFirestore();
      const userDocRef = doc(db, 'users', emailToUse.toLowerCase());
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userDataDoc = userDoc.data();
        if (userDataDoc && userDataDoc.email) {
          emailToUse = userDataDoc.email;
        } else {
          throw { code: 'auth/user-not-found' };
        }
      } else {
        throw { code: 'auth/user-not-found' };
      }
    }

    await signInWithEmailAndPassword(auth, emailToUse, userData.password)
    .then((res) => {
      setAuthorizationHeader(res.user.accessToken);
      dispatch({ type: SET_AUTHENTICATED });
      dispatch(getUserData());
      dispatch({ type: CLEAR_ERRORS });
      history.push('/portal', { fromLogin: true });
    })
    .catch((err) => {
      
      // Create a simplified user-friendly error message
      let errorPayload = {};

      // For most authentication errors, simply use a generic message
      // This covers: invalid-credential, user-not-found, wrong-password, etc.
      if (err.code && (
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-email')) {
        errorPayload.general = 'Invalid login.';
      } else if (err.code === 'auth/too-many-requests') {
        errorPayload.general = 'Too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/user-disabled') {
        errorPayload.general = 'This account has been disabled. Please contact support.';
      } else {
        errorPayload.general = 'Please check your credentials and try again.';
      }

      dispatch({
        type: SET_ERRORS,
        payload: errorPayload
      });
    });
  } catch (err) {
    // Handle cases where username lookup failed before attempting auth
    let errorPayload = { general: 'Invalid login. Please try again.' };
    dispatch({
      type: SET_ERRORS,
      payload: errorPayload
    });
  }
  /*
  axios
    .post('/login', userData)
    .then((res) => {
      setAuthorizationHeader(res.data.token);
      dispatch(getUserData());
      dispatch({ type: CLEAR_ERRORS });
      history.push('/');
    })
    .catch((err) => {
      dispatch({
        type: SET_ERRORS,
        payload: err.response.data
      });
    });
  */
};

export const signupUser = (newUserData, history) => (dispatch) => {
  dispatch({ type: LOADING_UI });

  // First check if username exists
  axios.get(`/user/${newUserData.handle.toLowerCase()}/check`)
    .then(res => {
      if (res.data.exists) {
        // Username already exists, throw error
        throw {
          response: {
            data: {
              handle: 'This username is already taken'
            }
          }
        };
      }

      // Username is available, proceed with signup
      return axios.post('/signup', newUserData);
    })
    .then((res) => {
      // After successful backend signup, sign in the user that was just created
      if (res.data.success) {
        const auth = getAuth();
        return signInWithEmailAndPassword(auth, newUserData.email, newUserData.password)
          .then((userCredential) => {
            // Update the user profile with the handle (username)
            return updateProfile(userCredential.user, {
              displayName: newUserData.handle
            }).then(() => {
              // Get the ID token to set up authentication
              return userCredential.user.getIdToken(true);
            });
          })
          .then((idToken) => {
            // Set authorization headers and dispatch auth actions
            setAuthorizationHeader(idToken);
            dispatch({ type: SET_AUTHENTICATED });
            dispatch(getUserData());
            dispatch({ type: CLEAR_ERRORS });
            
            // Wait for auth state to be confirmed, then redirect
            const auth = getAuth();
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              if (user) {
                unsubscribe(); // Clean up listener
                history.push('/portal', { fromSignup: true });
              }
            });
          });
      } else {
        // Handle unexpected response format
        dispatch({
          type: SET_ERRORS,
          payload: { general: 'Unexpected response from server' }
        });
      }
    })
    .catch((err) => {
      // console.error("Signup error:", err);

      // Check if it's our custom username error
      if (err.response && err.response.data && err.response.data.handle) {
        dispatch({
          type: SET_ERRORS,
          payload: err.response.data
        });
        return;
      }

      // Otherwise handle Firebase errors
      let errorPayload = {
        general: err.message || 'An error occurred during signup'
      };

      // Map Firebase errors to specific fields
      if (err.code === 'auth/email-already-in-use') {
        errorPayload.email = 'This email is already in use';
      } else if (err.code === 'auth/weak-password') {
        errorPayload.password = 'Password should be at least 6 characters';
      } else if (err.code === 'auth/invalid-email') {
        errorPayload.email = 'Please enter a valid email address';
      }

      dispatch({
        type: SET_ERRORS,
        payload: errorPayload
      });
    });
};

export const connectToLichess = (lichessToken) => (dispatch) => {
  setLichessAuthorizationHeader(lichessToken);
  dispatch({ type: CONNECT_USER_TO_LICHESS });
};

export const logoutUser = () => (dispatch) => {
  // ✅ Clear Authentication Tokens
  localStorage.removeItem('FBIdToken'); // Firebase authentication token
  localStorage.removeItem('LichessIdToken'); // Lichess OAuth token
  delete axios.defaults.headers.common['Authorization'];
  dispatch({ type: SET_UNAUTHENTICATED });
};

export const getUserData = () => (dispatch) => {
  dispatch({ type: LOADING_USER });
  axios
    .get('/user')
    .then((res) => {
      dispatch({
        type: SET_USER,
        payload: res.data
      });
    })
    .catch((err) => {
      // console.log(err);
    }); 
};

export const uploadImage = (formData) => (dispatch) => {
  dispatch({ type: LOADING_USER });
  axios
    .post('/user/image', formData)
    .then(() => {
      dispatch(getUserData());
    })
      .catch((err) => {
      // console.log(err);
    }); 
};

export const editUserDetails = (userDetails) => (dispatch) => {
  dispatch({ type: LOADING_USER });
  axios
    .post('/user', userDetails)
    .then(() => {
      dispatch(getUserData());
    })
    .catch((err) => {
      // console.log(err);
    }); 
};

export const markNotificationsRead = (notificationIds) => (dispatch) => {
  axios
    .post('/notifications', notificationIds)
    .then((res) => {
      dispatch({
        type: MARK_NOTIFICATIONS_READ
      });
    })
    .catch((err) => {
      // console.log(err);
    }); 
};

export const resetPassword = (userData, callback) => (dispatch) => {
  dispatch({ type: LOADING_UI });
  
  // Rate limiting check for password reset attempts
  const config = getRateLimit('PASSWORD_RESET', 'free');
  const rateKey = buildGlobalRateKey('anonymous', 'password_reset');
  
  const isLimited = checkRateLimit({
    key: rateKey,
    limit: config.limit,
    windowMs: config.windowMs
  });

  if (isLimited) {
    dispatch({
      type: SET_ERRORS,
      payload: {
        general: 'Too many password reset attempts. Please wait then try again.'
      }
    });
    return;
  }
  
  // axios
  // .post('/reset-password', userData)
  // .then((res) => {
  const auth = getAuth();
  sendPasswordResetEmail(auth, userData.email)
    .then(() => {
      dispatch({ type: CLEAR_ERRORS });
      if (callback) callback();
    })
    .catch((err) => {
      // console.error('Password reset error:', err);
      let errorPayload = {};

      if (err.code === 'auth/user-not-found') {
        errorPayload.email = 'No user found with this email address';
      } else if (err.code === 'auth/invalid-email') {
        errorPayload.email = 'Invalid email address format';
      } else {
        errorPayload.general = 'Error sending password reset email. Please try again.';
      }

      dispatch({
        type: SET_ERRORS,
        // payload: err.response.data
        payload: errorPayload
      });
    });
};

// Update password for authenticated user
export const updatePassword = (passwordData, successCallback, errorCallback) => (dispatch) => {
  dispatch({ type: LOADING_UI });

  const user = firebase.auth().currentUser;

  if (!user) {
    dispatch({
      type: SET_ERRORS,
      payload: { general: 'You must be logged in to update your password' }
    });
    if (errorCallback) errorCallback({ code: 'auth/user-not-found' });
    return;
  }

  // Get credentials with current password
  const credential = firebase.auth.EmailAuthProvider.credential(
    user.email,
    passwordData.currentPassword
  );

  // First reauthenticate the user
  user.reauthenticateWithCredential(credential)
    .then(() => {
      // User reauthenticated, now update password
      return user.updatePassword(passwordData.newPassword);
    })
    .then(() => {
      // Password updated successfully
      dispatch({ type: CLEAR_ERRORS });
      if (successCallback) successCallback();
    })
    .catch((err) => {
      // console.error('Error updating password:', err);
      dispatch({
        type: SET_ERRORS,
        payload: { general: 'Error updating password' }
      });
      if (errorCallback) errorCallback(err);
    });
};

const setAuthorizationHeader = (token) => {
  const FBIdToken = `Bearer ${token}`;
  localStorage.setItem('FBIdToken', FBIdToken);
  axios.defaults.headers.common['Authorization'] = FBIdToken;
};

const setLichessAuthorizationHeader = (token) => {
  if (token.startsWith('Bearer ')) {
    token = token.slice(7);
  }
  const LichessIdToken = `Bearer ${token}`;
  localStorage.setItem('LichessIdToken', LichessIdToken);
};

// New function to check if username exists
export const checkUsernameAvailable = async (handle) => {
  try {
    const response = await axios.get(`/user/${handle.toLowerCase()}/check`);
    return !response.data.exists; // Return true if username is available
  } catch (error) {
    // console.error('Error checking username:', error);
    return false; // Assume username is taken if there's an error
  }
};